import { ReactNode, useEffect, useState } from "react";
import { motion, useDragControls, useReducedMotion } from "framer-motion";
import { Mic, Bug, Sparkles, Maximize2, Minimize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  isInteractiveTarget,
  SheetCloseIcon,
  SheetDragHandle,
  SHEET_CLOSE_CLASS,
  SHEET_FULL_ON_MOBILE_CLASS,
  SHEET_GROW_CLASS,
  SHEET_HEADER_CLASS,
  SHEET_OVERLAY_TINT_CLASS,
  SHEET_SAFE_PB_CLASS,
  VOICE_PANEL_BODY_CLASS,
  VOICE_PANEL_FULLSCREEN_CLASS,
  VOICE_PANEL_H_CLASS,
  VOICE_PANEL_H_TABLET_CLASS,
  VOICE_PANEL_W_CLASS,
} from "@/components/ui/sheetChrome";
import { SHEET_PHONE_RISE, SHEET_PHONE_FALL, SHEET_INSTANT } from "@/components/ui/sheetMotion";
import { useIsPhone, useIsTablet } from "@/hooks/use-phone";
import { useContext } from "react";
import { AssistantContext } from "@/contexts/AssistantContext";
import { useCanUseAssistant } from "@/hooks/useCanUseAssistant";
import { useAssistantIsPage } from "@/hooks/useAssistantIsPage";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Props {
  tab: "ai" | "task" | "scouting";
  children: ReactNode;
  footer?: ReactNode;
  /** Hide the segmented toggle when in mid-flow (recording, processing, success) */
  hideTabs?: boolean;
  onClose?: () => void;
  /** Hide the admin task/scouting tab toggle entirely (worker single-flow). */
  singleTab?: boolean;
  /** Extra classes for the scrollable body (e.g. p-0 for the full-bleed AI chat). */
  bodyClassName?: string;
  /**
   * Optional confirmation guard. Return true to allow close, false to abort.
   * If returns true, the modal closes immediately.
   * To use the built-in AlertDialog confirm, pass `confirmOnClose={true}` instead.
   */
  onRequestClose?: () => boolean;
  /** When true, X / backdrop trigger an in-modal AlertDialog asking to discard changes. */
  confirmOnClose?: boolean;
  /**
   * La hoja se estira a pantalla completa EN EL TELÉFONO (ver
   * SHEET_FULL_ON_MOBILE_CLASS). Para cuando dentro ya hay algo que leer: el chat
   * lo enciende en cuanto se manda el primer mensaje. En tableta y escritorio no
   * hace nada — allí esto no es una hoja, es una ventana centrada o una tarjeta
   * en la esquina, y estirarla no tendría sentido.
   */
  expanded?: boolean;
  /**
   * Enseña el botón de PANTALLA COMPLETA (solo en tableta).
   *
   * No lo llevan las tres pestañas: en «Tarea por voz» y «Muestreo» se dicta una
   * frase y se confirma, y estirar eso a toda la pantalla solo deja más hueco en
   * blanco. El chat del asistente es el único donde hay algo que LEER, que es de
   * donde venía la petición.
   */
  fullscreenable?: boolean;
}

export function VoiceModalShell({
  tab,
  children,
  footer,
  hideTabs,
  onClose,
  singleTab,
  onRequestClose,
  confirmOnClose,
  bodyClassName,
  expanded = false,
  fullscreenable = false,
}: Props) {
  const ctx = useContext(AssistantContext);
  const setIsPanelOpen = ctx?.setIsPanelOpen ?? (() => {});
  const setActivePanel = ctx?.setActivePanel ?? (() => {});
  const { t } = useTranslation();
  // La pestaña «Asistente» solo se enseña a quien el backend deja pasar: un
  // perito la veía y cada mensaje moría en un 403.
  //
  // Y solo por debajo de `lg`: en escritorio el asistente es la subpágina
  // /advanced (entrada propia en la barra lateral), así que aquí el conmutador se
  // queda en «Tarea por voz» y «Muestreo» y no hay dos sitios para lo mismo.
  const assistantIsPage = useAssistantIsPage();
  const canChat = useCanUseAssistant();
  const showAssistantTab = canChat && !assistantIsPage;
  const [discardOpen, setDiscardOpen] = useState(false);
  const isPhone = useIsPhone();
  /* PANTALLA COMPLETA, SOLO EN TABLETA. En el teléfono ya la hay —`expanded` la
     estira sola en cuanto hay conversación— y en escritorio el asistente tiene
     su propia página (/advanced), así que un botón más sería una tercera forma
     de hacer lo mismo. */
  const isTablet = useIsTablet();
  const [fullscreen, setFullscreen] = useState(false);
  const canFullscreen = fullscreenable && isTablet;
  const isFullscreen = canFullscreen && fullscreen;

  /* Girar la tableta o estrechar la ventana puede sacarnos del rango; si eso
     pasa se vuelve solo, o quedaría una tarjeta a pantalla completa sin botón
     con el que salir de ella. */
  useEffect(() => {
    if (!canFullscreen) setFullscreen(false);
  }, [canFullscreen]);

  /* La cabecera y la barra de abajo se apagan por CSS (`body.voice-fullscreen`,
     ver index.css) y no ocultándolas desde aquí: son de otro árbol. Se quita al
     desmontar pase lo que pase —cerrar, cambiar de pestaña, navegar—, o la app
     se quedaría sin barras y sin nada abierto que lo explicara. */
  useEffect(() => {
    if (!isFullscreen) return;
    document.body.classList.add("voice-fullscreen");
    return () => document.body.classList.remove("voice-fullscreen");
  }, [isFullscreen]);
  /* «Reducir movimiento» del sistema: se respeta de verdad, no se ignora.
     Hasta ahora solo lo miraba `PageTransition`. */
  const reduceMotion = useReducedMotion();
  const dragControls = useDragControls();

  const doClose = () => {
    onClose?.();
    // Safety: clear any leftover Radix body lock so the page stays interactive.
    if (typeof document !== "undefined") {
      document.body.style.pointerEvents = "";
    }
    setIsPanelOpen(false);
  };

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (onRequestClose) {
      if (onRequestClose()) doClose();
      return;
    }
    if (confirmOnClose) {
      setDiscardOpen(true);
      return;
    }
    doClose();
  };

  return (
    <>
      {/* Backdrop — SOLO teléfono. De tableta en adelante NO hay velo: la tarjeta
          vive anclada a la esquina como extensión del botón del micro y la app de
          detrás sigue siendo navegable con la ventana abierta (petición del
          usuario 31-08-2026: en tableta, igual que en escritorio). En el teléfono
          se queda, porque allí la hoja ocupa media pantalla y sin velo no se
          entiende que hay algo delante. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={handleClose}
        className={cn("fixed inset-0 z-40 md:hidden", SHEET_OVERLAY_TINT_CLASS)}
      />

      {/* EL SUELO DE LA PANTALLA COMPLETA (petición del usuario 31-08-2026: «solo
          quiero ver la tarjeta del asistente»).

          La ventana deja libre la franja del reloj —no puede meterse debajo de
          la barra de estado— y por ese hueco asomaba la esquina redonda de la
          tarjeta de la página de detrás. Se tapa con un fondo blanco a toda la
          pantalla: como la ventana también es blanca, el hueco deja de leerse
          como un hueco y la barra de estado queda sobre la misma superficie.

          Blanco liso y NO el velo oscuro de las hojas: aquí no hay nada detrás
          que enseñar a medias, y una tinta gris sobre otra tinta gris volvería a
          parecer que hay algo asomando. Y sin `onClick`: en pantalla completa se
          sale por el botón, no tocando un borde de 20 px. */}
      {isFullscreen && <div aria-hidden className="fixed inset-0 z-40 bg-white" />}

      {/* Modal card — la posición la lleva este envoltorio fijo para que el
          transform de framer (y/scale) de la tarjeta no pueda pelearse con ella.
          Teléfono = hoja inferior. De TABLETA en adelante = anclada abajo a la
          derecha, igual que en escritorio (antes en tableta era una ventana
          centrada con velo).

          El envoltorio es `pointer-events-none` y solo la tarjeta recupera los
          toques: sin velo, todo lo que queda alrededor —la barra de abajo, la
          página, la navegación— se sigue pudiendo tocar con la ventana abierta.

          EN TABLETA SE SUBE POR ENCIMA DE LA BARRA DE ABAJO. `bottom-6` vale en
          escritorio porque allí no hay barra flotante (`MobileDock` es
          `lg:hidden`); en tableta la tarjeta caería justo encima de ella y del
          micro que le sobresale. `--mobile-dock-clearance` es el mismo hueco que
          reserva el contenido de todas las páginas, así que si la barra cambia de
          alto esto se mueve con ella. */}
      <div
        className={cn(
          "fixed z-50 inset-x-0 bottom-0 pointer-events-none",
          /* A pantalla completa se vuelve a la colocación del teléfono —pegada
             abajo y a todo el ancho—, que es justo lo que hace falta: el anclaje
             a la esquina es lo contrario de lo que se ha pedido. */
          !isFullscreen && "md:inset-auto md:right-6 md:bottom-[var(--mobile-dock-clearance)] lg:bottom-6",
        )}
      >
      <motion.div
        /* En el teléfono sube entera desde abajo, como el resto de hojas. Los
           tiempos salen de `sheetChrome` para que las hojas de framer y las de CSS
           no vayan cada una a su ritmo. En la ventana centrada eso arrastraría la
           tarjeta por media pantalla, así que ahí hace el zoom de siempre — por eso
           hay dos variantes y no una. */
        initial={isPhone ? { y: "100%" } : { opacity: 0, scale: 0.97 }}
        animate={isPhone ? { y: 0 } : { opacity: 1, scale: 1 }}
        exit={
          isPhone
            ? { y: "100%", transition: reduceMotion ? SHEET_INSTANT : SHEET_PHONE_FALL }
            : { opacity: 0, scale: 0.97, transition: { duration: reduceMotion ? 0 : 0.2 } }
        }
        transition={
          reduceMotion
            ? SHEET_INSTANT
            : isPhone
              ? SHEET_PHONE_RISE
              : { duration: 0.22, ease: "easeOut" }
        }
        /* Arrastrar para cerrar. Lo lleva framer y no el gesto compartido porque
           aquí los transformes ya son suyos. `dragListener={false}`: el gesto solo
           arranca desde la franja del tirador, así que arrastrar el contenido
           sigue siendo hacer scroll. Sin tope abajo y clavado arriba: la hoja baja
           con el dedo pero no sube. */
        /* También a pantalla completa. El gesto solo arranca desde la cabecera y
           el tirador —nunca desde el cuerpo—, así que no compite con el scroll de
           la conversación. */
        drag={isPhone ? "y" : false}
        dragListener={false}
        dragControls={dragControls}
        dragConstraints={{ top: 0 }}
        dragElastic={0}
        onDragEnd={(_, info) => {
          if (info.offset.y > 120 || (info.velocity.y > 500 && info.offset.y > 24)) handleClose();
        }}
        className={cn(
          "pointer-events-auto",
          VOICE_PANEL_W_CLASS,
          // De tableta en adelante el zoom nace de la esquina inferior derecha
          // —donde está anclada— en vez del centro de la tarjeta.
          "md:origin-bottom-right",
          // Mismo redondeo que el selector de Reportar plaga (GlassSheet): 28 arriba en
          // hoja inferior, 24 en ventana centrada.
          "rounded-t-[28px] md:rounded-[24px]",
          // Alto FIJO y el MISMO para las tres pestañas en todos los tamaños de
          // pantalla (ver VOICE_PANEL_H_CLASS). Antes el chat medía 88/76dvh y
          // Tarea por voz solo tenía tope, así que encogía hasta su contenido y
          // las dos ventanas del mismo conmutador se veían de alturas distintas.
          // Fijo y no tope, además, porque el chat reparte su interior con flex:
          // con la altura indefinida el composer acababa cortado bajo el borde.
          VOICE_PANEL_H_CLASS,
          /* Tableta más baja. DESPUÉS del alto de arriba: mismo grupo de
             tailwind-merge, gana la última. Lo que protege la constante —que
             las TRES pestañas del conmutador midan lo mismo— sigue en pie,
             porque esta carcasa las envuelve a las tres. */
          VOICE_PANEL_H_TABLET_CLASS,
          /* DESPUÉS del ancho y del alto de arriba: mismo grupo de
             tailwind-merge, gana la última. */
          isFullscreen && VOICE_PANEL_FULLSCREEN_CLASS,
          // El segundo estado: la misma hoja hasta debajo de la barra de estado.
          // Va DESPUÉS para ganarle al alto de arriba, y solo pinta en el teléfono.
          expanded && SHEET_FULL_ON_MOBILE_CLASS,
          !reduceMotion && SHEET_GROW_CLASS,
          "flex flex-col overflow-hidden",
          "bg-white border border-[#e2ecf5]",
          "shadow-[0_24px_60px_-15px_rgba(15,42,76,0.35)]"
        )}
        /* `willChange` junto al `touchAction` que ya estaba: capa propia mientras
           se mueve, para que el compositor la desplace en vez de volver a
           rasterizarla en cada fotograma. */
        style={{ touchAction: "pan-y", willChange: "transform" }}
      >
        {/* Asa de arrastre, igual que en el resto de hojas inferiores. Antes en su sitio
            había una franja de degradado azul que no aparece en ninguna otra ventana.
            Se queda TAMBIÉN a pantalla completa: es lo que sigue diciendo «esto es
            una hoja y se baja», y sin ella la ventana parecía otra pantalla. */}
        <div
          className="md:hidden pt-2.5 shrink-0 bg-white cursor-grab active:cursor-grabbing"
          style={{ touchAction: "none" }}
          onPointerDown={(e) => isPhone && dragControls.start(e)}
        >
          <SheetDragHandle />
        </div>

        {/* Cabecera con el mismo cromo que el selector de Reportar plaga: el título en
            grande a la izquierda —antes era una etiqueta diminuta en mayúsculas— y la X
            en su círculo gris. */}
        {/* La cabecera entera baja la hoja, no solo la rayita: es la parte que
            nadie usa para leer y donde el pulgar cae solo. Aquí no vale la franja
            compartida (`SheetGrabZone`) porque el movimiento lo lleva framer, así
            que se comparte solo el filtro: un gesto que empieza sobre la X o
            sobre una pestaña se deja pasar como toque. */}
        <div
          className={cn("relative z-20 shrink-0 bg-white", SHEET_HEADER_CLASS, "pb-2")}
          style={isPhone ? { touchAction: "none" } : undefined}
          onPointerDown={(e) => {
            if (!isPhone || isInteractiveTarget(e.target)) return;
            dragControls.start(e);
          }}
        >
          <div className="flex items-center justify-between gap-3 mb-2">
            <h2
              className="font-['Poppins'] font-bold truncate pr-1"
              style={{ fontSize: 20, color: "#13314A" }}
            >
              {tab === "ai"
                ? t("advanced.title", "Ulture Assistant")
                : tab === "task"
                ? t("voiceModal.headerTask")
                : t("voiceModal.headerScouting")}
            </h2>
            {/* Pantalla completa y cerrar, en ese orden: la X se queda donde
                siempre ha estado, en el borde. Un botón nuevo entre el título y
                la X movería la salida de sitio. */}
            <div className="flex items-center gap-2 shrink-0">
              {canFullscreen && (
                <button
                  type="button"
                  onClick={() => setFullscreen((v) => !v)}
                  className={cn("relative z-30 pointer-events-auto", SHEET_CLOSE_CLASS)}
                  aria-label={
                    fullscreen
                      ? t("voiceModal.fullscreenExit", "Salir de pantalla completa")
                      : t("voiceModal.fullscreenEnter", "Pantalla completa")
                  }
                  title={
                    fullscreen
                      ? t("voiceModal.fullscreenExit", "Salir de pantalla completa")
                      : t("voiceModal.fullscreenEnter", "Pantalla completa")
                  }
                >
                  {fullscreen ? (
                    <Minimize2 className="w-[17px] h-[17px]" strokeWidth={2.2} />
                  ) : (
                    <Maximize2 className="w-[17px] h-[17px]" strokeWidth={2.2} />
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={handleClose}
                className={cn("relative z-30 pointer-events-auto", SHEET_CLOSE_CLASS)}
                aria-label={t("common.close")}
              >
                <SheetCloseIcon />
              </button>
            </div>
          </div>

          {!hideTabs && !singleTab && (
            <div className="flex rounded-full bg-[#eef4fb] p-1 gap-1">
              {showAssistantTab && (
              <button
                type="button"
                onClick={() => setActivePanel("aiChat")}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all",
                  tab === "ai"
                    ? "bg-white shadow-sm text-black"
                    : "text-black hover:text-black"
                )}
              >
                <Sparkles className="w-4 h-4 shrink-0" />
                <span className="truncate">{t("voiceModal.tabAssistant", "Asistente")}</span>
              </button>
              )}
              <button
                type="button"
                onClick={() => setActivePanel("taskVoice")}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all",
                  tab === "task"
                    ? "bg-white shadow-sm text-black"
                    : "text-black hover:text-black"
                )}
              >
                <Mic className="w-4 h-4 shrink-0" />
                <span className="truncate">{t("voiceModal.tabTask")}</span>
              </button>
              <button
                type="button"
                onClick={() => setActivePanel("scoutingVoice")}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all",
                  tab === "scouting"
                    ? "bg-white shadow-sm text-black"
                    : "text-black hover:text-black"
                )}
              >
                <Bug className="w-4 h-4 shrink-0" />
                <span className="truncate">{t("voiceModal.tabScouting")}</span>
              </button>
            </div>
          )}
        </div>

        {/* Body. Con la caja a alto fijo, el contenido corto (Tarea por voz) dejaba
            un hueco muerto debajo: se centra en vertical mientras quepa. El chat
            pasa su propio bodyClassName y no se ve afectado. */}
        <div
          className={cn(
            "flex-1 overflow-y-auto bg-[#f7fafd] px-4 py-4",
            /* SIN PIE el hueco de la barra de Android lo reserva el cuerpo, o
               el último botón se queda debajo de ella: es lo que pasaba en
               «Muestreo por voz», donde la tarjeta amarilla de «Voz» quedaba
               partida por los tres botones (29-08-2026). */
            !footer && "pb-[max(1rem,var(--safe-bottom))]",
            tab !== "ai" && VOICE_PANEL_BODY_CLASS,
            bodyClassName,
          )}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          /* `SHEET_SAFE_PB_CLASS`: sin él, en un Android con la barra de 3
             botones «Cancelar» y «Detener» quedaban DEBAJO de ella. Esta hoja se
             montaba el pie a mano en vez de usar el compartido, y por eso se
             quedó fuera del arreglo que el resto ya tenía. */
          <div className={cn("shrink-0 bg-white border-t border-[#eef3f8] px-4 pt-3", SHEET_SAFE_PB_CLASS)}>
            {footer}
          </div>
        )}
      </motion.div>
      </div>

      {/* Discard confirm dialog */}
      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent className="z-[60]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("voiceModal.discard.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("voiceModal.discard.message")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("voiceModal.discard.keepEditing")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDiscardOpen(false);
                // Defer one tick so Radix can run its close cleanup
                // (body pointer-events) before the modal subtree unmounts.
                setTimeout(() => doClose(), 0);
              }}
            >
              {t("voiceModal.discard.leave")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
